import React, { useEffect, useState } from 'react';
import { DateTime } from 'luxon';

import { getUserTimeCreditLifePointActivities } from '@services/user/userService';
import { WaringRound } from '@util/svgicon';
import { timeZoneAbbreviated } from '@util/config';
import Spinner from '@util/spinner';

import Transcript from './Transcript';

const ITEMS_PER_PAGE = 10;

const RightSection = () => {
  const [activities, setActivities] = useState(undefined);
  const [rideId, setRideId] = useState(undefined);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastData, setLastData] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchActivities = async () => {
      setLoading(true);
      const response = await getUserTimeCreditLifePointActivities(currentPage, ITEMS_PER_PAGE);
      const data = response?.tc_lp_activities || [];

      setActivities(data);
      setLastData(data.length < ITEMS_PER_PAGE);
      if (data.length === 0) {
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
      }
      setLoading(false);
    };

    fetchActivities();
  }, [currentPage]);

  const handlePageChange = (direction) => {
    setActivities(undefined);
    setLastData(false);
    setCurrentPage((prevPage) => prevPage + direction);
  };

  return (
    <div className="w-full">
      <Spinner loading={loading} text="Please wait..." />
      <div className="flex h-full p-[36px] flex-col items-start gap-[24px] backdrop-blur-3xl">
        {rideId ? (
          <Transcript rideId={rideId} close={() => setRideId(undefined)} />
        ) : (
          <TranscriptSection
            activities={activities}
            setRideId={setRideId}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            lastData={lastData}
          />
        )}
      </div>
    </div>
  );
};

const TranscriptSection = ({ activities, setRideId, currentPage, handlePageChange, lastData }) => {
  return (
    <>
      <div className="flex justify-between w-full items-center border-b pb-[8px] border-b-gray border-solid">
        <p className="text-lg font-medium text-gray">History</p>
      </div>
      <div className="w-full">
        <table className="w-full">
          <thead>
            <tr>
              <th className="text-center p-[8px]">Date/Time</th>
              <th className="text-center p-[8px]">Source</th>
              <th className="text-center p-[8px]">TC</th>
              <th className="text-center p-[8px]">Balance</th>
              <th className="text-center p-[8px]">XP</th>
              <th className="text-center p-[8px]">LP</th>
              <th className="text-center p-[8px]"></th>
            </tr>
          </thead>
          <tbody>
            {activities?.map((activity) => (
              <TcHistoryTableRow key={activity.id} activity={activity} setRideId={setRideId} />
            ))}
          </tbody>
        </table>
        <Pagination
          currentPage={currentPage}
          handlePageChange={handlePageChange}
          lastData={lastData}
        />
      </div>
    </>
  );
};

const TcHistoryTableRow = ({ activity, setRideId }) => {
  const datetime = DateTime.fromISO(activity.created_at);
  const showLP = activity.credit_type === 'Life Point';
  const isDebit = showLP || activity.entry === 'debit';
  const showXP = !!activity.time_creditable?.xp_amount;

  return (
    <tr className="m-2">
      <td className="text-center p-[8px]">
        <p className="text-[16px] leading-[20px] text-gray">{datetime.toLocaleString(DateTime.DATE_FULL)}</p>
        <p className="text-[16px] leading-[20px] text-black">
          {datetime.toLocaleString(DateTime.TIME_SIMPLE)} {timeZoneAbbreviated()}
        </p>
      </td>
      <td className="text-center p-[8px]">
        <p className="text-[16px] leading-[20px] text-gray">
          {activity.time_creditable?.type === 'Ride' ? 'Mission attempt' : 'Payment'}
        </p>
      </td>
      <td className={`text-center p-[8px] text-[16px] leading-[20px] ${isDebit ? 'text-red' : 'text-green'}`}>
        {!isDebit && '+'}{showLP ? '----' : `${activity.amount} TC`}
      </td>
      <td className="text-center p-[8px] text-[16px] leading-[20px] text-black">
        {!isDebit && '+'}{activity.balance} {showLP ? 'LP' : 'TC'}
      </td>
      <td className={`text-center p-[8px] text-[16px] leading-[20px] ${showXP ? 'text-green' : 'text-black'}`}>
        {showXP ? `+${activity.time_creditable.xp_amount || ''}XP` : '----'}
      </td>
      <td className={`text-center p-[8px] text-[16px] leading-[20px] ${showLP ? 'text-green' : 'text-black'}`}>
        {showLP ? `+${activity.amount || ''}LP` : '----'}
      </td>
      <td className="text-center p-[8px]">
        {activity.time_creditable?.type === 'Ride' && (
          <button onClick={() => setRideId(activity.time_creditable.id)} className="bg-black text-white rounded-full flex gap-1 p-[8px]">
            <WaringRound />
            Details
          </button>
        )}
      </td>
    </tr>
  );
};

const Pagination = ({ currentPage, handlePageChange, lastData }) => (
  <div className={`flex ${currentPage !== 1 ? 'justify-between' : 'justify-end'} w-full mt-4`}>
    {currentPage !== 1 && (
      <button onClick={() => handlePageChange(-1)} className="bg-black text-white flex gap-1 p-[8px] py-2 px-4 rounded">
        Previous Page
      </button>
    )}
    {!lastData && (
      <button onClick={() => handlePageChange(1)} className="bg-black text-white flex gap-1 p-[8px] py-2 px-4 rounded">
        Next Page
      </button>
    )}
  </div>
);

export default RightSection;
